import React from 'react';
import { X, User, Calendar } from 'lucide-react';

const AlertDetailModal = ({ alert, onClose, onAcknowledge, getPriorityColor, getPriorityIcon }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString();
  };

  const renderAuditDetails = () => (
    <>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Action</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.action}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Category</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.category?.replace(/_/g, ' ')}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Level</label>
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mt-1 ${
            alert?.level === 'critical' ? 'bg-red-100 text-red-800' :
            alert?.level === 'error' ? 'bg-orange-100 text-orange-800' :
            alert?.level === 'warning'? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {alert?.level?.toUpperCase()}
          </span>
        </div>

        {alert?.description && (
          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.description}</p>
          </div>
        )}

        {alert?.resource_type && (
          <div>
            <label className="text-sm font-medium text-gray-700">Resource Type</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.resource_type}</p>
          </div>
        )}

        {alert?.resource_id && (
          <div>
            <label className="text-sm font-medium text-gray-700">Resource ID</label>
            <p className="text-sm text-gray-600 mt-1 font-mono text-xs">{alert?.resource_id}</p>
          </div>
        )}

        {alert?.ip_address && (
          <div>
            <label className="text-sm font-medium text-gray-700">IP Address</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.ip_address}</p>
          </div>
        )}

        {alert?.user && (
          <div>
            <label className="text-sm font-medium text-gray-700">User</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.user?.full_name} ({alert?.user?.email})</p>
          </div>
        )}
      </div>
    </>
  );

  const renderComplianceDetails = () => (
    <>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Alert Type</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.alert_type?.replace(/_/g, ' ')}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Severity</label>
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mt-1 ${
            alert?.severity === 'critical' ? 'bg-red-100 text-red-800' :
            alert?.severity === 'high' ? 'bg-orange-100 text-orange-800' :
            alert?.severity === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {alert?.severity?.toUpperCase()}
          </span>
        </div>

        {alert?.due_date && (
          <div>
            <label className="text-sm font-medium text-gray-700">Due Date</label>
            <p className="text-sm text-gray-900 mt-1">{formatDate(alert?.due_date)}</p>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700">Message</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.message}</p>
        </div>

        {alert?.created_by_user && (
          <div>
            <label className="text-sm font-medium text-gray-700">Created By</label>
            <p className="text-sm text-gray-900 mt-1">
              {alert?.created_by_user?.full_name} ({alert?.created_by_user?.email})
            </p>
          </div>
        )}

        {alert?.is_resolved && alert?.resolved_by_user && (
          <>
            <div>
              <label className="text-sm font-medium text-gray-700">Resolved By</label>
              <p className="text-sm text-gray-900 mt-1">
                {alert?.resolved_by_user?.full_name} ({alert?.resolved_by_user?.email})
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Resolved At</label>
              <p className="text-sm text-gray-900 mt-1">{formatDate(alert?.resolved_at)}</p>
            </div>
          </>
        )}

        {alert?.metadata?.resolution_notes && (
          <div>
            <label className="text-sm font-medium text-gray-700">Resolution Notes</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.metadata?.resolution_notes}</p>
          </div>
        )}
      </div>
    </>
  );

  const renderEnvironmentalDetails = () => (
    <>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Alert Type</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.alert_type?.replace(/_/g, ' ')}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Severity</label>
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mt-1 ${
            alert?.severity === 'critical' ? 'bg-red-100 text-red-800' :
            alert?.severity === 'high' ? 'bg-orange-100 text-orange-800' :
            alert?.severity === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {alert?.severity?.toUpperCase()}
          </span>
        </div>

        {alert?.sensor_type && (
          <div>
            <label className="text-sm font-medium text-gray-700">Sensor Type</label>
            <p className="text-sm text-gray-900 mt-1">{alert?.sensor_type?.replace(/_/g, ' ')}</p>
          </div>
        )}

        {alert?.device && (
          <div>
            <label className="text-sm font-medium text-gray-700">Device</label>
            <p className="text-sm text-gray-900 mt-1">
              {alert?.device?.device_name} - {alert?.device?.location_zone}
            </p>
          </div>
        )}

        {alert?.threshold_value && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Threshold</label>
              <p className="text-sm text-gray-900 mt-1">{alert?.threshold_value}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Actual Value</label>
              <p className="text-sm font-semibold text-red-600 mt-1">{alert?.actual_value}</p>
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700">Message</label>
          <p className="text-sm text-gray-900 mt-1">{alert?.message}</p>
        </div>

        {alert?.is_acknowledged && alert?.acknowledged_user && (
          <>
            <div>
              <label className="text-sm font-medium text-gray-700">Acknowledged By</label>
              <p className="text-sm text-gray-900 mt-1">
                {alert?.acknowledged_user?.full_name} ({alert?.acknowledged_user?.email})
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Acknowledged At</label>
              <p className="text-sm text-gray-900 mt-1">{formatDate(alert?.acknowledged_at)}</p>
            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={getPriorityColor(alert?.priority)}>
              {getPriorityIcon(alert?.priority)}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Alert Details</h2>
              <p className="text-sm text-gray-600">
                {alert?.type?.replace(/_/g, ' ')?.toUpperCase()} - {alert?.priority?.toUpperCase()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Title */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {alert?.title || alert?.message?.substring(0, 100)}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              Created {formatDate(alert?.created_at)}
            </div>
          </div>

          {/* Type-specific Details */}
          {alert?.type === 'audit' && renderAuditDetails()}
          {alert?.type === 'compliance' && renderComplianceDetails()}
          {alert?.type === 'environmental' && renderEnvironmentalDetails()}

          {/* Metadata */}
          {alert?.metadata && Object.keys(alert?.metadata)?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Additional Information</label>
              <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(alert?.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {!alert?.isAcknowledged && alert?.type !== 'audit' && (
            <button
              onClick={() => {
                onAcknowledge(alert);
                onClose();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Acknowledge
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;